import { NavLink, useLocation } from "react-router-dom";
import tw, { css, styled } from "twin.macro";
import { useAppTheme } from "app/providers/AppThemeProvider";
import appLinks, { profileLink } from "app/constants/appLinks";
import { ReactComponent as LogoutIcon } from "app/assets/icons/logout.svg";
import { gotoSignout } from "app/lib/logout";
import { useActiveProfile } from "app/providers/ActiveProfileProvider";
import { useVerificationStatus } from "app/providers/VerificationStatusProvider";
import useAppLinks from "app/hooks/useAppLinks";
import { useRef } from "react";

const profileLinks = [profileLink];
const links = appLinks.concat(profileLinks);

export default function DashboardSideNav() {
  const theme = useAppTheme();
  const activeProfile = useActiveProfile();
  const verificationStatus = useVerificationStatus();
  const location = useLocation();
  const logout = () => gotoSignout();

  const trimLinks = useAppLinks(links);
  const divRef = useRef<HTMLDivElement>(null);

  return (
    <Container
      css={
        activeProfile?.profile_type === "business" &&
        verificationStatus?.status !== "verified" &&
        tw`h-[calc(100vh-108px)]`
      }
    >
      <NavigationSection>
        {trimLinks.map(({ children, label, icon: Icon, url, exact }) => {
          const hasSubnav = Boolean(children && children.length);
          const openSubnav = (children ?? []).some((r) =>
            location.pathname.startsWith(r.url)
          );

          const activeSubnavIndex = children?.findIndex((subLink) =>
            location.pathname.startsWith(subLink.url)
          );

          return (
            <List key={label}>
              <Link
                to={url}
                end={exact ?? true}
                className={openSubnav ? "subnav-active" : undefined}
              >
                <div tw="size-[20px] mr-[10px] relative">
                  <Icon className={theme} />

                  {hasSubnav && openSubnav && (
                    <Pointer
                      ref={divRef}
                      style={{
                        height: `${getSubnavHeight(activeSubnavIndex)}px`,
                        transition: "height 180ms ease",
                      }}
                    ></Pointer>
                  )}
                </div>
                {activeProfile?.profile_type === "business" &&
                  (Boolean(
                    label === "My wallets" &&
                      activeProfile.permissions?.[0] !== "**"
                  )
                    ? "Balances"
                    : label)}
                {activeProfile?.profile_type === "personal" && label}
              </Link>

              {hasSubnav && openSubnav && (
                <SubNav className="subnav">
                  {children?.map((subLink) => {
                    return (
                      <Link key={subLink.label} to={subLink.url}>
                        {subLink.label}
                      </Link>
                    );
                  })}
                </SubNav>
              )}
            </List>
          );
        })}
      </NavigationSection>

      <button css={LinkStyles} onClick={logout}>
        <LogoutIcon className={theme} />
        Log out
      </button>
    </Container>
  );
}

const List = styled.li`
  &:nth-of-type(3) {
    > a {
      &.active,
      &.subnav-active {
        svg {
          path {
            fill: #000;
            fill-opacity: 1;
          }

          &.dark {
            path {
              fill: #fff;
              fill-opacity: 1;
            }
          }
        }
      }
    }
  }
`;

const getSubnavHeight = (index?: number) => {
  if (typeof index !== "number" || index < 0) return 14;
  return 10 + 19 + index * 38;
};

const Container = styled.nav`
  ${tw`w-full h-page py-[24px] px-[32px] flex flex-col border-r`};
  ${tw`bg-white `};
  ${tw`dark:bg-black dark:border-r-white12`};
`;

const NavigationSection = styled.ul`
  ${tw`flex-grow`};

  &,
  li {
    ${tw`w-full`};
  }
`;

const LinkStyles = css`
  ${tw`w-full h-[46px] rounded-[8px] px-[16px] flex items-center text-[1.6rem] text-textLow font-semibold mb-[8px] last-of-type:mb-0`};
  ${tw`dark:text-white32`};

  svg {
    ${tw`w-[20px] h-[20px] mr-[10px]`};
  }
`;

const Link = styled(NavLink)`
  ${LinkStyles};

  &.active,
  &.subnav-active {
    ${tw`bg-greyScale10 text-text-high`};
    ${tw`dark:bg-greyScale90 dark:text-white`};
  }

  &.subnav-active {
    ${tw`bg-transparent dark:bg-transparent`};
  }
`;

const SubNav = styled.div`
  ${tw`pl-[17px]`};

  ${Link} {
    ${tw`mb-0 h-[38px] pl-[30px]`};
    border-radius: 8px 8px 8px 8px;

    &.active {
      ${tw`border-l-black95 dark:border-l-white95 dark:bg-greyScale90`};
    }
  }
`;

const Pointer = styled.div`
  ${tw`w-[13px] mt-[4px] rounded-bl-[4px] border-2 border-t-0 border-r-0 border-black95 dark:border-white95 last-of-type:mb-0 absolute left-[50%] before:bg-current before:w-[2px] before:h-[4px] before:block before:absolute before:top-[-1px] before:left-[-19%] before:rounded-full `}

  ${tw`after:bg-current after:w-[2px] after:h-[2px] after:block after:absolute after:bottom-[-2px] after:left-[87%] after:rounded-full`};
`;
